import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { IOrganizationNode } from './models/organization-chart.types';
import { DEFAULT_ORGANIZATION_CHART } from './constants/organization-chart';
import { IProgressOrganizationChart } from '../../../../features/scenario/components/decide/model/decide.model';

@Component({
  selector: 'app-organization-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './organization-chart.component.html',
  styleUrl: './organization-chart.component.scss',
})
export class OrganizationChartComponent implements OnChanges {
  @Input() organizationChart?: IOrganizationNode;
  @Input() showCheckBox = false;
  @Input() showPhaseIndicator = false;
  @Input() maxNumberOfChecked: string | undefined = '';
  @Input() progress: IProgressOrganizationChart[] | null = []; // Used to show progress in the chart

  @Output() nodeSelectionChange = new EventEmitter<IOrganizationNode[]>();

  data!: IOrganizationNode;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['organizationChart']) {
      this.data = this.organizationChart || this.getDefaultData();
    }

    // Check if maxNumberOfChecked changed to "Whole organization"
    // Only trigger if it's not the first change (initial value) and the new value is "Whole organization"
    if (
      changes['maxNumberOfChecked'] &&
      !changes['maxNumberOfChecked'].firstChange &&
      changes['maxNumberOfChecked'].currentValue === 'Whole organization'
    ) {
      this.selectAllNodes();
    }

    // Update nodes with progress data when progress changes
    if (changes['progress'] && this.progress && this.data) {
      this.updateNodesWithProgress();
    }
  }

  getDefaultData() {
    return DEFAULT_ORGANIZATION_CHART;
  }

  // Method to select all nodes when "Whole organization" is selected
  selectAllNodes(): void {
    if (this.data) {
      this.setAllNodesSelected(this.data, true);
      const selectedNodes = this.getSelectedNodes(this.data);
      this.nodeSelectionChange.emit(selectedNodes);
    }
  }

  // Helper method to recursively set all nodes as selected/unselected
  setAllNodesSelected(node: IOrganizationNode, selected: boolean): void {
    node.selected = selected;

    if (node.children) {
      for (const child of node.children) {
        this.setAllNodesSelected(child, selected);
      }
    }
  }

  // Helper method to check if a node should be disabled
  isNodeDisabled(node: IOrganizationNode): boolean {
    if (!this.showCheckBox) return false;

    // If "Whole organization" is selected, disable all checkboxes
    if (this.maxNumberOfChecked === 'Whole organization') {
      return true;
    }

    const maxAllowed = this.maxNumberOfChecked
      ? parseInt(this.maxNumberOfChecked, 10)
      : 0;
    if (maxAllowed <= 0) return false;

    // If node is already selected, it shouldn't be disabled
    if (node.selected) return false;

    // If we've reached the limit, disable unselected nodes
    const currentlySelected = this.getSelectedNodes(this.data);
    return currentlySelected.length >= maxAllowed;
  }

  onNodeCheckboxChange(node: IOrganizationNode, event: Event): void {
    const target = event.target as HTMLInputElement;
    const isChecked = target.checked;

    // If maxNumberOfChecked is null, undefined, or empty, don't allow any changes
    if (!this.maxNumberOfChecked || this.maxNumberOfChecked.trim() === '') {
      target.checked = node.selected!; // Revert the checkbox state
      return;
    }

    // If "Whole organization" is selected, don't allow changes
    if (this.maxNumberOfChecked === 'Whole organization') {
      return;
    }

    // Parse maxNumberOfChecked to number for numeric limits
    const maxAllowed = this.maxNumberOfChecked
      ? parseInt(this.maxNumberOfChecked, 10)
      : 0;

    // If trying to check a node and there's a numeric limit
    if (isChecked && maxAllowed > 0) {
      // Get currently selected nodes
      const currentlySelected = this.getSelectedNodes(this.data);

      // Check if adding this node would exceed the limit
      if (currentlySelected.length >= maxAllowed) {
        // Prevent the selection
        target.checked = false;
        alert(`You can only select up to ${maxAllowed} members.`);
        return;
      }
    }

    // Update node selection
    node.selected = isChecked;

    // Get updated selected nodes
    const selectedNodes = this.getSelectedNodes(this.data);

    // Emit the change event
    this.nodeSelectionChange.emit(selectedNodes);
  }

  // Helper method to get all selected nodes
  private getSelectedNodes(
    node: IOrganizationNode | undefined
  ): IOrganizationNode[] {
    if (!node) return [];

    const selectedNodes: IOrganizationNode[] = [];

    if (node.selected) {
      selectedNodes.push(node);
    }

    if (node.children) {
      for (const child of node.children) {
        selectedNodes.push(...this.getSelectedNodes(child));
      }
    }

    return selectedNodes;
  }

  // Method to update nodes with progress data
  updateNodesWithProgress(): void {
    if (!this.progress || !this.data) return;

    this.updateNodeProgress(this.data);
  }

  // Helper method to recursively update nodes with progress data
  updateNodeProgress(node: IOrganizationNode): void {
    // Find matching progress for current node

    if (!this.progress) return;

    const progressData = this.progress.find(
      (progress_user) => progress_user.sk_user === node.sk_user
    );

    if (progressData) {
      node.level = progressData.level;
      node.score = progressData.score;
    }

    // Recursively update children
    if (node.children) {
      for (const child of node.children) {
        this.updateNodeProgress(child);
      }
    }
  }

  isPhaseActive(nodeLevel: string, phaseToCheck: string): boolean {
    const phaseOrder = ['Awareness', 'Interest', 'Experimentation', 'Adoption'];
    const currentIndex = phaseOrder.indexOf(nodeLevel);
    const checkIndex = phaseOrder.indexOf(phaseToCheck);

    return currentIndex >= checkIndex && currentIndex !== -1;
  }
}
